import { Label } from '@primer/react';
import clsx from 'clsx';
import { SnapshotType } from '@upright/common/model/versions/types';
import { compareVersions } from '@upright/common/utils';
import { useGetVersions } from '@upright/ui/api/model/versions';
import { CheckStreamlineCoreLine, Cog1, FileList, LayoutGrid, VerticalSlider2StreamlineCore } from '@upright/ui/icons';
import { Checkbox } from '@upright/ui/common/generic/components/checkbox';
import { useIsTeam, useValueSetSelectorAvailable, useViewSetting } from '@upright/ui/common/generic/hooks';
import { useState } from 'react';
import {
  Button as AriaButton,
  Menu,
  MenuItem,
  MenuSection,
  MenuTrigger,
  Popover,
  type Selection,
  Separator,
  SubmenuTrigger,
} from 'react-aria-components';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { isRenderViewPathname } from '@upright/common/renderer/renderer-settings';
import {
  setModelVersionAndRelease,
  setValuesSelectorOpen,
  setViewSetting,
  setViewSettingsModalOpen,
} from '../../../../untidy/redux/actions/actions';
import { getValueSetName, getValuesSelectorOpen, getVersionAndRelease } from '../../../../untidy/redux/selectors';
import { CreateCompanyModal } from '../create-company-modal';
import { startMenuCategories, type StartMenuAction, type StartMenuLink, type StartMenuSubcategory } from './menu-data';
import { useStartMenuPageContextItems } from './page-context';
import styles from './start-menu.module.css';

const UNIFIED_SHELL_ITEM_ID = 'unified-shell';

export function StartMenu() {
  const isTeam = useIsTeam();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const pageContextItems = useStartMenuPageContextItems();
  const unifiedShell = useViewSetting('internalAdmin.unifiedShell');
  const showVersionSelector = useViewSetting('internalAdmin.headerVersionSelector');
  const valueSetAvailable = useValueSetSelectorAvailable();
  const valueSet = useSelector(getValueSetName);
  const valuesSelectorOpen = useSelector(getValuesSelectorOpen);
  const [isCreateCompanyOpen, setIsCreateCompanyOpen] = useState(false);

  if (!isTeam) {
    return null;
  }
  // The render views are loaded by upshot's headless browser with the
  // requesting user's auth, so for team users this floating button would
  // get printed into exported PDFs/PNGs (PLT-5061).
  if (isRenderViewPathname(pathname)) {
    return null;
  }

  const actionHandlers: Record<StartMenuAction, () => void> = {
    'create-company': () => {
      setIsCreateCompanyOpen(true);
    },
  };

  const handleLink = (link: StartMenuLink) => {
    if (link.action) {
      actionHandlers[link.action]();
      return;
    }
    if (!link.url) {
      return;
    }
    const isExternal = link.external ?? /^https?:\/\//.test(link.url);
    if (isExternal) {
      window.open(link.url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(link.url);
    }
  };

  const renderLinkItem = (categoryLabel: string, link: StartMenuLink) => (
    <MenuItem
      key={`${categoryLabel}:${link.label}:${link.url ?? link.action ?? ''}`}
      className={styles.item}
      onAction={() => {
        handleLink(link);
      }}
    >
      <span className={styles['item-label']}>{link.label}</span>
      {link.badges?.map(badge => (
        <Label key={badge} variant="default" size="small">
          {badge}
        </Label>
      ))}
    </MenuItem>
  );

  const renderSubcategory = (categoryLabel: string, sub: StartMenuSubcategory) => (
    <SubmenuTrigger key={`${categoryLabel}:${sub.label}`}>
      <MenuItem id={`subcategory-${categoryLabel}-${sub.label}`} className={styles.item}>
        <span>{sub.label}</span>
        <span className={styles['item-chevron']} aria-hidden>
          ▸
        </span>
      </MenuItem>
      <Popover className={styles.popover} placement="end top" offset={4}>
        <Menu className={styles.menu}>{sub.links.map(link => renderLinkItem(sub.label, link))}</Menu>
      </Popover>
    </SubmenuTrigger>
  );

  return (
    <>
      <MenuTrigger>
        <AriaButton className={styles.trigger} aria-label="Internal tools">
          <LayoutGrid aria-hidden />
        </AriaButton>
        <Popover className={styles.popover} placement="top start" offset={8}>
          <Menu className={styles.menu}>
            {/*
             * The team-only settings cluster, of which View settings is the first row.
             * This menu is the one home for internal tooling, so the unified shell's
             * account menu carries nothing team-only and reads the same for a team account
             * as it does for a customer's (PLT-6732). The legacy dark header keeps copies
             * of its own, and they go when that shell does (PLT-6596).
             */}
            <MenuItem className={styles.item} onAction={() => dispatch(setViewSettingsModalOpen(true))}>
              <Cog1 className={styles['item-icon']} aria-hidden />
              <span className={styles['item-label']}>View settings</span>
            </MenuItem>
            {/*
             * The row reads `useValueSetSelectorAvailable` rather than this menu's own team gate,
             * so that one hook stays the single answer to "is the value set offered anywhere".
             * Retiring or widening the feature is then one edit, not a hunt for call sites.
             */}
            {valueSetAvailable && (
              <MenuItem
                className={styles.item}
                onAction={() => {
                  dispatch(setValuesSelectorOpen(!valuesSelectorOpen));
                }}
              >
                <VerticalSlider2StreamlineCore className={styles['item-icon']} aria-hidden />
                <span className={styles['item-label']}>Value set</span>
                <span className={styles['item-value']}>{valueSet}</span>
              </MenuItem>
            )}
            {/* Rendered conditionally so the versions query only ever fires for a team user who asked. */}
            {showVersionSelector && <ProductDataVersionSubmenu />}
            {/*
             * The one View setting with a row of its own. While the unified investor shell
             * is being built (Unified UI Phase 1) it covers only the home-view routes, so a
             * team user crosses between it and the legacy chrome many times a day, and the
             * View settings modal is a long list away each time. The row toggles the same
             * `internalAdmin.unifiedShell` setting `useUnifiedShell` reads, so this switch
             * and the modal's cannot disagree. A checkable section gives the row
             * `menuitemcheckbox` semantics, with the design system's checkbox as its
             * visual, the way a checkable row in `dropdowns/MenuButton` renders. The menu
             * closes on select so the shell swap is seen rather than happening under it.
             *
             * The row says "Unified UI", which is the project's name and what the team
             * calls the thing (PLT-6648, Janne's review of the shell on staging). The
             * setting key stays `internalAdmin.unifiedShell` deliberately: it is persisted
             * per user in localStorage, so renaming it would switch everyone who has the
             * shell on back off. So the label and the key differ here on purpose, and the
             * View settings modal's label for the same key was moved to match this one.
             */}
            <MenuSection
              selectionMode="multiple"
              selectedKeys={unifiedShell ? [UNIFIED_SHELL_ITEM_ID] : []}
              onSelectionChange={(keys: Selection) => {
                dispatch(
                  setViewSetting('internalAdmin.unifiedShell', keys === 'all' || keys.has(UNIFIED_SHELL_ITEM_ID)),
                );
              }}
              shouldCloseOnSelect
            >
              <MenuItem id={UNIFIED_SHELL_ITEM_ID} className={styles.item} textValue="Unified UI">
                {({ isSelected }) => (
                  <>
                    <span className={styles['item-check']}>
                      <Checkbox isSelected={isSelected} />
                    </span>
                    <span className={styles['item-label']}>Unified UI</span>
                  </>
                )}
              </MenuItem>
            </MenuSection>
            <Separator className={styles.divider} />
            {startMenuCategories.map(category => (
              <SubmenuTrigger key={category.label}>
                <MenuItem id={`category-${category.label}`} className={styles.item}>
                  <span>{category.label}</span>
                  <span className={styles['item-chevron']} aria-hidden>
                    ▸
                  </span>
                </MenuItem>
                <Popover className={styles.popover} placement="end top" offset={4}>
                  <Menu className={styles.menu}>
                    {category.links.map(link => renderLinkItem(category.label, link))}
                    {category.subcategories && category.subcategories.length > 0 && (
                      <>
                        <Separator className={styles.divider} />
                        {category.subcategories.map(sub => renderSubcategory(category.label, sub))}
                      </>
                    )}
                  </Menu>
                </Popover>
              </SubmenuTrigger>
            ))}
            {pageContextItems.length > 0 && (
              <>
                <Separator className={styles.divider} />
                {pageContextItems}
              </>
            )}
          </Menu>
        </Popover>
      </MenuTrigger>
      <CreateCompanyModal
        isOpen={isCreateCompanyOpen}
        onClose={() => {
          setIsCreateCompanyOpen(false);
        }}
      />
    </>
  );
}

/**
 * The product-data version selector as a submenu: one row naming the version in use,
 * opening the list beside it. Choosing a version clears the release, because a version
 * picked by hand is not the one a release pins.
 *
 * A single-select `MenuSection` rather than plain rows, so the list carries
 * `menuitemradio` semantics and the check mark is read off the same selection state that
 * paints it. The caller gates this on the `internalAdmin.headerVersionSelector` View
 * setting, so neither the row nor its query exists for a team user who has not asked for
 * it.
 */
function ProductDataVersionSubmenu() {
  const dispatch = useDispatch();
  const { version } = useSelector(getVersionAndRelease);
  const { data: versions } = useGetVersions(all =>
    all
      .filter(candidate => candidate.type !== SnapshotType.DELETED)
      .map(candidate => candidate.number)
      .sort((a, b) => compareVersions(b, a)),
  );

  if (!versions || versions.length === 0) {
    return null;
  }

  return (
    <SubmenuTrigger>
      <MenuItem id="product-data-version" className={styles.item}>
        <FileList className={styles['item-icon']} aria-hidden />
        <span className={styles['item-label']}>Product data version</span>
        <span className={styles['item-value']}>{version}</span>
        <span className={styles['item-chevron']} aria-hidden>
          ▸
        </span>
      </MenuItem>
      <Popover className={styles.popover} placement="end top" offset={4}>
        <Menu className={clsx(styles.menu, styles['menu-scrollable'])}>
          <MenuSection
            selectionMode="single"
            selectedKeys={[version]}
            onSelectionChange={(keys: Selection) => {
              if (keys === 'all') {
                return;
              }
              const [selected] = [...keys];
              if (typeof selected === 'string' && selected !== version) {
                dispatch(setModelVersionAndRelease(selected, null));
              }
            }}
            shouldCloseOnSelect
          >
            {versions.map(candidate => (
              <MenuItem key={candidate} id={candidate} className={styles.item} textValue={candidate}>
                {({ isSelected }) => (
                  <>
                    <span className={styles['item-mark']}>{isSelected && <CheckStreamlineCoreLine aria-hidden />}</span>
                    <span className={styles['item-label']}>{candidate}</span>
                  </>
                )}
              </MenuItem>
            ))}
          </MenuSection>
        </Menu>
      </Popover>
    </SubmenuTrigger>
  );
}
